import React from "react";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../../api/auth";
import {
  AuthPageShell,
  authLinkSx,
  authPrimaryButtonSx,
  authSecondaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function ConfirmEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const initialEmail = location.state?.email || searchParams.get("email") || "";
  const token = String(searchParams.get("token") || "").trim();
  const initialCode = String(searchParams.get("code") || "").replace(/\D/g, "").slice(0, 6);

  const [email, setEmail] = React.useState(initialEmail);
  const [code, setCode] = React.useState(initialCode);
  const [message, setMessage] = React.useState(location.state?.message || "");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(Boolean(token));
  const [resending, setResending] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);
  const autoConfirmAttempted = React.useRef(false);

  const confirm = React.useCallback(async ({ nextEmail, nextCode, nextToken }) => {
    const payload = {
      email: String(nextEmail || "").trim().toLowerCase(),
      code: String(nextCode || "").trim(),
      token: String(nextToken || "").trim(),
    };

    if (!payload.token && (!payload.email || !payload.code)) {
      setError("Ingresá tu email y el código de 6 números.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await authApi.confirmEmail(payload);
      setConfirmed(true);
      setMessage(data?.message || "Email confirmado. Ya podés ingresar.");
    } catch (err) {
      setError(err.message || "No se pudo confirmar el email.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!token || autoConfirmAttempted.current) return;
    autoConfirmAttempted.current = true;
    confirm({ nextEmail: initialEmail, nextToken: token });
  }, [token, initialEmail, confirm]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await confirm({ nextEmail: email, nextCode: code });
  };

  const handleResend = async () => {
    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setError("Ingresá un email válido para reenviar el código.");
      return;
    }

    try {
      setResending(true);
      setError("");
      const data = await authApi.resendConfirmation({ email: cleanEmail });
      setMessage(data?.message || "Si el email existe, se envió un nuevo código.");
    } catch (err) {
      setError(err.message || "No se pudo reenviar el código.");
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthPageShell
      title="Confirmar email"
      subtitle={confirmed ? "Tu correo ya quedó confirmado." : "Ingresá el código que te enviamos."}
      icon={<MarkEmailReadRoundedIcon />}
      helpLabel="¿No encontrás el código?"
      help="Revisá spam o correo no deseado. El código tiene 6 números. También podés abrir el enlace del email; si venció, reenviá un código nuevo desde esta pantalla."
      footer={
        <Link component={RouterLink} to="/login" sx={authLinkSx}>
          Volver a ingresar
        </Link>
      }
    >
      {message ? <Alert severity={confirmed ? "success" : "info"}>{message}</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading && token && !confirmed ? (
        <Alert severity="info">Confirmando tu email...</Alert>
      ) : null}

      {!confirmed ? (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={1.5}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              fullWidth
              autoFocus={!token && !initialEmail}
              autoComplete="email"
              disabled={loading}
              inputProps={{ inputMode: "email", autoCapitalize: "none", spellCheck: "false" }}
            />
            <TextField
              label="Código"
              name="code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              required
              fullWidth
              autoFocus={!token && Boolean(initialEmail)}
              autoComplete="one-time-code"
              disabled={loading}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
              helperText="6 números"
            />

            <Button
              type="submit"
              disabled={loading || code.length !== 6 || !String(email || "").includes("@")}
              variant="contained"
              size="large"
              sx={authPrimaryButtonSx}
            >
              {loading ? "Confirmando..." : "Confirmar email"}
            </Button>
            <Button
              type="button"
              onClick={handleResend}
              disabled={resending || loading}
              sx={authSecondaryButtonSx}
            >
              {resending ? "Reenviando..." : "Reenviar código"}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Button
          onClick={() => navigate("/login", { replace: true, state: { message: "Email confirmado. Ya podés ingresar." } })}
          variant="contained"
          size="large"
          fullWidth
          sx={authPrimaryButtonSx}
        >
          Iniciar sesión
        </Button>
      )}
    </AuthPageShell>
  );
}
